import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export interface SubscriptionListItem {
  id: number;
  user_id: string;
  registration_date?: string | null;
  subscription_type?: string | null;
  stripe_customer_id?: string | null;
  invites_sent_today?: number;
  messages_sent_today?: number;
  // Contexte organisation B2B (null pour un compte B2C solo ou si B2B_ENABLED=false).
  is_business?: boolean | null;
  organization_id?: number | null;
  organization_name?: string | null;
  organization_tier?: string | null;
  member_role?: string | null;
  purchased_seats?: number | null;
  active_seats?: number | null;
}

export interface PaginatedSubscriptionsResponse {
  items: SubscriptionListItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface StripeSubscription {
  id: string;
  status: string;
  created?: string | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end: boolean;
  canceled_at?: string | null;
  amount?: number | null;
  currency?: string | null;
  interval?: string | null;
  interval_count?: number | null;
}

export interface StripeInvoice {
  id: string;
  number?: string | null;
  status: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  charge?: string | null;
  created?: string | null;
  hosted_invoice_url?: string | null;
  invoice_pdf?: string | null;
  subscription?: string | null;
}

export async function listSubscriptions(
  navigate: NavigateFunction,
  params?: { page?: number; size?: number }
) {
  const q = new URLSearchParams();
  q.set("page", String(params?.page ?? 1));
  q.set("size", String(params?.size ?? 50));
  const url = `${API_URL}/subscriptions?${q.toString()}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des abonnements");
  return res.json() as Promise<PaginatedSubscriptionsResponse>;
}

export interface StripeCharge {
  id: string;
  status: string;
  amount: number;
  amount_refunded: number;
  currency: string;
  paid: boolean;
  refunded: boolean;
  created?: string | null;
  description?: string | null;
  failure_message?: string | null;
  receipt_url?: string | null;
  invoice?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
}

/**
 * One Stripe list of the user billing page.
 * - `customer_missing_in_current_mode`: the stored Stripe customer does not
 *   exist in the Stripe mode of the current key (e.g. a test customer after the
 *   switch to live keys) — nothing to list, not an error;
 * - `no_customer`: the account has no Stripe customer at all (backoffice 404).
 */
export interface StripeList<T> {
  items: T[];
  customer_missing_in_current_mode?: boolean;
  no_customer?: boolean;
}

async function listUserStripeObjects<T>(
  navigate: NavigateFunction,
  userId: string,
  resource: "stripe-charges" | "stripe-subscriptions" | "stripe-invoices",
  errorMessage: string
): Promise<StripeList<T>> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${encodeURIComponent(userId)}/${resource}`,
    { method: "GET" },
    navigate
  );
  // 404 = « Aucun customer Stripe pour cet utilisateur » : un état, pas une panne.
  if (res?.status === 404) return { items: [], no_customer: true };
  if (!res?.ok) throw new Error((await safeError(res)) || errorMessage);
  return (await res.json()) as StripeList<T>;
}

export function listUserStripeCharges(navigate: NavigateFunction, userId: string) {
  return listUserStripeObjects<StripeCharge>(
    navigate,
    userId,
    "stripe-charges",
    "Erreur lors du chargement des transactions Stripe"
  );
}

export function listUserStripeSubscriptions(navigate: NavigateFunction, userId: string) {
  return listUserStripeObjects<StripeSubscription>(
    navigate,
    userId,
    "stripe-subscriptions",
    "Erreur lors du chargement des subscriptions Stripe"
  );
}

export function listUserStripeInvoices(navigate: NavigateFunction, userId: string) {
  return listUserStripeObjects<StripeInvoice>(
    navigate,
    userId,
    "stripe-invoices",
    "Erreur lors du chargement des factures Stripe"
  );
}

// Contexte organisation B2B d'un compte (cf. GET /api/admin/users/{id}/organization).
// null si B2C solo ou B2B_ENABLED=false côté backoffice.
export interface OrganizationContext {
  id: number;
  name: string;
  tier: string | null;
  is_business: boolean | null;
  member_role: string | null;
  purchased_seats?: number | null;
  active_seats?: number | null;
}

export async function getUserOrganization(
  navigate: NavigateFunction,
  userId: string
): Promise<OrganizationContext | null> {
  const res = await apiFetch(
    `${API_URL}/admin/users/${encodeURIComponent(userId)}/organization`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement de l'organisation");
  const data = (await res.json()) as { organization: OrganizationContext | null };
  return data.organization;
}

export async function cancelStripeSubscription(
  navigate: NavigateFunction,
  userId: string,
  subscriptionId: string
) {
  const res = await apiFetch(
    `${API_URL}/admin/users/${encodeURIComponent(userId)}/stripe-subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
    { method: "POST" },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors de l'annulation");
  }
  return res.json();
}

export async function refundStripeInvoice(
  navigate: NavigateFunction,
  userId: string,
  invoiceId: string
) {
  const res = await apiFetch(
    `${API_URL}/admin/users/${encodeURIComponent(userId)}/stripe-invoices/${encodeURIComponent(invoiceId)}/refund`,
    { method: "POST" },
    navigate
  );
  if (!res?.ok) {
    const msg = await safeError(res);
    throw new Error(msg || "Erreur lors du remboursement");
  }
  return res.json();
}

async function safeError(res: Response | null | undefined): Promise<string | null> {
  if (!res) return null;
  try {
    const data = await res.json();
    return typeof data?.detail === "string" ? data.detail : null;
  } catch {
    return null;
  }
}
